/**
 * A starting repository that belongs to one learner.
 *
 * Every level's workspace has been the same bytes for everybody since the
 * course was written, which is the right trade for practice and the wrong one
 * for a credential. It means a learner who finishes level 10 can hand their
 * finished workspace to anybody, and the server — which grades the submitted
 * result and nothing else — will confirm it for whoever sends it. That is the
 * one path in this product from cheating to a claim a stranger is invited to
 * believe (docs/cheating.md), and it is what this module closes.
 *
 * ## Why the salt has to be in the history, and not a marker
 *
 * The obvious design is a per-learner token written somewhere in the
 * repository that the grader checks. It does not work, and it is worth writing
 * down why, because it is the design everybody reaches for first.
 *
 * A marker is something the client writes. The learner's own browser has to
 * know the salt in order to write it, so a learner knows their own — which
 * means they can take somebody else's finished workspace, overwrite the marker
 * with their own, and submit it. The marker proves only that the sender knew
 * their own salt, which they always do.
 *
 * What actually binds work to a learner is **ancestry**. If the commit their
 * work is built on is unique to them, every commit they make on top of it has
 * a different hash from anybody else's, and the work itself — not a label
 * beside it — is what carries the identity. Copying somebody's finished
 * repository gives you their base and their descendants, and there is no way
 * to put your own base underneath their work without rewriting every commit,
 * which is the same thing as doing the level.
 *
 * So the salt is a commit, and the check that enforces it is one the grader
 * already runs: server/src/verification/grade.ts seeds a fresh workspace and
 * refuses any submission whose `main` does not match the one it just built.
 * Salting both sides of that seed turns an existing check into copy detection,
 * with nothing new to bypass.
 *
 * ## What the server may never take from the browser
 *
 * The salt is derived here from a secret the browser does not have, and at
 * grading time it is re-derived **from the account that owns the attempt** —
 * never read out of the submission, never taken from a request field. A salt
 * that arrived with the work would be a number the sender chose, and this
 * whole module would be decoration.
 *
 * The learner's browser does receive its own salt, because the guest has to
 * seed with it. That is fine and is not a leak: knowing your own salt lets you
 * build your own repository, which is what you are supposed to do.
 */
/**
 * The derivation's own version, mixed into every salt.
 *
 * Bumping it gives every account a new starting repository for every level,
 * which invalidates work in progress — so it moves only when the derivation
 * itself is wrong, never to force a re-grade. It is separate from
 * VERIFICATION_VERSION, which versions the *grading contract*: a check can
 * change without everybody's workspace having to.
 */
export const SEED_SALT_VERSION = 1;
/** 128 bits, hex. Long enough not to collide, short enough to read in a log. */
const SALT_HEX_LENGTH = 32;
/**
 * UTF-8, because there is no TextEncoder here either.
 *
 * Every input this module hashes is ASCII in practice — a UUID, a mission
 * slug, and a generated secret — but the secret is configuration somebody
 * types, so it is encoded properly rather than assumed.
 */
function utf8(text) {
    const bytes = [];
    for (let i = 0; i < text.length; i++) {
        let code = text.charCodeAt(i);
        if (code >= 0xd800 && code <= 0xdbff && (text.charCodeAt(i + 1) & 0xfc00) === 0xdc00) {
            code = 0x10000 + ((code - 0xd800) << 10) + (text.charCodeAt(i + 1) - 0xdc00);
            i++;
        }
        else if (code >= 0xd800 && code <= 0xdfff) {
            code = 0xfffd;
        }
        if (code < 0x80)
            bytes.push(code);
        else if (code < 0x800)
            bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
        else if (code < 0x10000)
            bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        else
            bytes.push(0xf0 | (code >> 18), 0x80 | ((code >> 12) & 0x3f), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    }
    return Uint8Array.from(bytes);
}
function hex(buffer) {
    return Array.from(new Uint8Array(buffer), (b) => b.toString(16).padStart(2, '0')).join('');
}
/**
 * This account's salt for this level.
 *
 * ## Generations, and why stability is not always what you want
 *
 * `generation` is 0 for a teaching level and never moves, which is the
 * stability the paragraph below describes: reset the level, clear the browser,
 * come back on another device, and the same starting repository comes back, so
 * work already done still grades.
 *
 * For a **checkpoint** that is the wrong behaviour. A checkpoint is an
 * assessment, and somebody who fails it, reads the scenario they were given and
 * asks for another go must not be handed the identical repository back: that is
 * sitting the same paper twice with the answers in hand. Each fresh attempt
 * bumps the generation, so it is a new repository; while one attempt is open the
 * generation does not move, so a reset inside that attempt still grades.
 *
 * Which behaviour a mission gets is decided by the caller from the mission's
 * kind, and never by the browser: the generation travels with the attempt, so
 * the grader re-derives the repository the learner was actually issued rather
 * than whichever one they would prefer to be graded against.
 *
 * HMAC rather than a random value in a column, for two reasons. It is
 * reproducible — the grader re-derives it at grading time from the account on
 * the attempt, so nothing has to be stored, nothing can go stale, and a
 * database row cannot disagree with a repository. And it is stable: a learner
 * who resets a level, clears their browser, or comes back on another device
 * gets the same starting repository back, so the work they already did still
 * grades. A stored random salt would have to answer "what happens when it is
 * lost", and the answer would be "their finished level stops being gradeable".
 *
 * Web Crypto because this one function has to run in three places — the
 * Worker, the browser and the grading container — and they agree on `crypto`
 * and on nothing else.
 */
export async function deriveSeedSalt(secret, accountId, missionId, generation = 0) {
    if (!secret || secret.length < 32)
        throw new Error('seed salt secret is missing or too short');
    if (!accountId || !missionId)
        throw new Error('seed salt needs an account and a mission');
    if (!Number.isInteger(generation) || generation < 0)
        throw new Error('seed salt generation must be a whole number');
    const key = await crypto.subtle.importKey('raw', utf8(secret), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
    // The parts are joined with a character that cannot appear in either id, so
    // that no two different pairs can produce the same message. Ids are a UUID
    // and a mission slug; a newline is in neither.
    const message = `eos-seed-salt\n${SEED_SALT_VERSION}\n${accountId}\n${missionId}\n${generation}`;
    return hex(await crypto.subtle.sign('HMAC', key, utf8(message))).slice(0, SALT_HEX_LENGTH);
}
/** Exactly what a salt looks like on the wire, so nothing else is ever treated as one. */
export function isSeedSalt(value) {
    return typeof value === 'string' && new RegExp(`^[0-9a-f]{${SALT_HEX_LENGTH}}$`).test(value);
}
/**
 * The identity the salt commit is made under.
 *
 * Fixed, and deliberately not the learner's: a commit author is shown in
 * `git log` and belongs to the person who wrote the code. This commit was not
 * written by the learner, so it does not carry their name — and a fixed
 * identity is also one less thing that has to be reproduced identically on two
 * machines for the hashes to match.
 */
const SALT_AUTHOR = 'EngineerOS <workspace@engineeros.dev>';
/**
 * A fixed timestamp, because a commit hash includes its dates.
 *
 * The guest makes this commit when the learner starts the level and the
 * grader makes it seconds before grading, hours or days later. If either used
 * "now", the two would hash differently and every honest submission would fail
 * the baseline check. So the only thing that varies between two learners'
 * commits is the salt itself, which is the entire point.
 *
 * 2026-09-14T00:00:00Z, the day this was written, in git's own format.
 */
const SALT_DATE = '1789004800 +0000';
/**
 * The commands that turn a freshly seeded workspace into this learner's own.
 *
 * Returned as a script rather than run here, because the two callers run
 * commands in very different ways — one over a serial line into a browser VM,
 * one through `docker exec` — and the one thing that must not differ between
 * them is what git is actually asked to do. One string, two runners, identical
 * bytes.
 *
 * Every value that feeds the commit hash is pinned: author, committer, both
 * dates, the message and the tree. Two machines running this with the same
 * salt produce the same commit id, which is what the grader's baseline check
 * compares.
 */
export function seedSaltScript(salt, repoDir = 'storefront', branch = 'main') {
    if (!isSeedSalt(salt))
        throw new Error('refusing to salt a workspace with a malformed salt');
    // Plumbing, never `git commit`, and this is the part that took a level
    // analysis to get right.
    //
    // `git commit` commits wherever HEAD is, and on all three pilot levels HEAD
    // is a *feature* branch when the seed finishes — so the salt would land on
    // the learner's working branch instead of main. On level 19 that is not a
    // cosmetic difference: the branch gets pushed, so the salt would be rebased
    // onto origin/main and turn up in the pull request the level asks them to
    // open, in the diff a reviewer reads.
    //
    // `commit-tree` + `update-ref` writes a commit object and moves one branch,
    // and touches nothing else: HEAD stays where it is, the working tree and the
    // index are not read or written, and — the reason level 10 survives this at
    // all — HEAD's reflog gains no entry, so `HEAD@{1}` still resolves to the
    // commit that level teaches you to recover with it.
    //
    // The new commit reuses the branch's existing tree, so it changes no file.
    const author = `GIT_AUTHOR_NAME='EngineerOS' GIT_AUTHOR_EMAIL='workspace@engineeros.dev' ` +
        `GIT_COMMITTER_NAME='EngineerOS' GIT_COMMITTER_EMAIL='workspace@engineeros.dev' ` +
        `GIT_AUTHOR_DATE='${SALT_DATE}' GIT_COMMITTER_DATE='${SALT_DATE}'`;
    const ref = `refs/heads/${branch}`;
    return [
        `cd ${repoDir}`,
        // A reflog message, because the entry is written whether we want it or not
        // (`core.logAllRefUpdates=false` does not suppress an append to a log that
        // already exists) and a blank line in `git reflog show main` is stranger to
        // a learner than a described one.
        `salted=$(${author} git commit-tree -p ${ref} -m 'Prepare this workspace' -m 'workspace: ${salt}' ${ref}^{tree})`,
        `git update-ref -m 'prepare this workspace' ${ref} "$salted"`,
    ].join('\n');
}
export { SALT_AUTHOR, SALT_DATE };
/**
 * Turn a freshly seeded workspace into this learner's own, or leave it alone.
 *
 * The two grading paths — the Cloudflare sandbox and the Docker reference
 * service — both call this immediately after `seedWorkspace` and before they
 * read `main`, so the baseline they compare a submission against is the salted
 * one. Sharing the call, and not just the script, is deliberate: the failure
 * this design has to avoid is one runner salting and the other not, which
 * would refuse every honest submission on that path.
 *
 * A null salt is the ordinary case for every level that is not on
 * SALTED_MISSIONS, and does nothing at all.
 *
 * The guest does not call this — it folds `seedSaltScript` into the single
 * batched exec that already extracts the tarball, because a second round trip
 * over the VM's serial line costs more than the commit does. Both sides still
 * run the same generated script, which is the part that has to match.
 */
export async function applySeedSalt(ws, salt, repoDir = 'storefront') {
    if (salt === null)
        return;
    const r = await ws.runScript(seedSaltScript(salt, repoDir), { timeoutMs: 60_000 });
    // Loudly. A salt that silently failed to apply would leave the workspace
    // seeded from the shared bytes, and the baseline check would then compare a
    // salted submission against an unsalted baseline and fail an honest learner.
    if (r.code !== 0)
        throw new Error(`could not salt the workspace (exit ${r.code}): ${r.stderr || r.stdout}`);
}
//# sourceMappingURL=salt.js.map